import { Grid } from "@mui/material";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { ArrowRightIconButton } from "components/Primitives";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { GroupErpNumber } from "components/group/groups-erp-number";
import { ClearFilterButton } from "../../../components/Buttons/clear-filter-button";
import SearchField from "../../../components/SearchField";
import { CustomTable } from "../../../components/Tables/custom-table";
import { useBaseDataGridStyles } from "../../../style/baseDataGrid.style";
import { getGroupTypeTranslation } from "../../../utils/group.helper";
import { GroupConverter } from "../../../domain/converter/group.converter";
import { IGroupLean } from "../../../domain/group/group-lean";
import { GroupTypeFilter, GroupTypeFilterValues } from "../group-filter/group-type-filter.component";
import { useGroupTableContext } from "./group-table.provider";

const GroupTableToolbar: FC = () => {
  const { t } = useTranslation();
  const { classes } = useBaseDataGridStyles();
  const { tablePagination, setTablePagination, setFilter, filter, isFilterDirty, resetFilter } = useGroupTableContext();

  const getGroupTypeFilterValue = (): GroupTypeFilterValues | undefined => {
    return GroupConverter.groupTypesToGroupTypeFilterValues(filter.groupType);
  };

  const setGroupTypeFilter = (value: GroupTypeFilterValues | undefined): void => {
    setTablePagination({ ...tablePagination, page: 0 });
    setFilter({ ...filter, groupType: GroupConverter.groupTypeFilterValuesToGroupTypes(value) });
  };

  return (
    <Grid direction="row" container className={classes.search}>
      <Grid className={classes.searchGrid} container spacing={4}>
        <Grid item>
          <GroupTypeFilter value={getGroupTypeFilterValue()} onChange={setGroupTypeFilter} />
        </Grid>
        {isFilterDirty && (
          <Grid item>
            <ClearFilterButton onClick={resetFilter} />
          </Grid>
        )}
        <Grid className={classes.searchGrid} item>
          <SearchField
            tooltip={t("basedata.groups.table.search.name")}
            submitSearch={true}
            inputTimeout={0}
            initialValue={filter?.nameQuery}
            onSearchSubmit={(value: string) => {
              setTablePagination({ ...tablePagination, page: 0 });
              setFilter({ ...filter, nameQuery: value });
            }}
            placeholder={t("basedata.groups.table.search.name")}
          />
        </Grid>
        <Grid className={classes.searchGrid} item>
          <SearchField
            tooltip={t("basedata.groups.table.search.erp")}
            submitSearch={true}
            inputTimeout={0}
            initialValue={filter?.erpQuery}
            onSearchSubmit={(value: string) => {
              setTablePagination({ ...tablePagination, page: 0 });
              setFilter({ ...filter, erpQuery: value });
            }}
            placeholder={t("basedata.groups.table.search.erp")}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const GroupTable: FC = () => {
  const { t } = useTranslation();
  const { groups, isLoading, tablePagination, setTablePagination, onShowGroupDetail } = useGroupTableContext();

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("basedata.groups.table.name"),
      flex: 1,
    },
    {
      field: "erpNumber",
      headerName: t("basedata.groups.edit.erpNumber"),
      flex: 1,
      renderCell: (params) => <GroupErpNumber erpNumber={(params.row as IGroupLean).erpNumber} />,
    },
    {
      field: "agencyName",
      headerName: t("basedata.group.agency_title"),
      flex: 1,
    },
    {
      field: "displayAddress",
      headerName: t("basedata.groups.edit.address"),
      flex: 1,
    },
    {
      field: "groupType",
      headerName: t("basedata.groups.table.type"),
      flex: 1,
      renderCell: (params) => {
        const groupType = (params.row as IGroupLean).groupType;
        return groupType !== null ? getGroupTypeTranslation(groupType, t) : null;
      },
    },
    {
      field: "__actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <ArrowRightIconButton onClick={() => onShowGroupDetail((params.row as IGroupLean).erpNumber)} />
      ),
    },
  ];

  return (
    <CustomTable
      columns={columns}
      rows={groups}
      loading={isLoading}
      getRowId={(row) => row.erpNumber}
      paginationModel={{ page: tablePagination.page, pageSize: tablePagination.pageSize }}
      onPaginationModelChange={(model: GridPaginationModel) =>
        setTablePagination({ ...tablePagination, page: model.page, pageSize: model.pageSize })
      }
      rowCount={tablePagination.numberOfRecords}
      pageSizeOptions={[50, 100]}
      paginationMode="server"
      noRowsLabel={t("basedata.groups.table.empty")}
      slots={{ toolbar: GroupTableToolbar }}
    />
  );
};

